/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import LOCAL_STORAGE_KEYS, {
  SESSION_STORAGE_KEYS,
} from "constants/STORAGE_KEYS";
import useEventRouting from "hooks/useEventRouting";
import useRouting from "hooks/useRouting";
import useTryCatch from "hooks/useTryCatch";
import { getInvitationDetails } from "lib/invitationApi";
import { loginMagicLink } from "lib/loginApi";
import { getProfile, logout } from "lib/userApi";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "routes";
import { normalize } from "utils/capitalize";
import {
  isOnEventSwitcher,
  isOnNoAccessPage,
  redirectToEventSwitcher,
  redirectToNoAccessPage,
} from "utils/eventSwitcher";
import extractParamsFromURL from "utils/extractParamsFromURL";
import extractToken from "utils/extractToken";
import useLocalStorage from "utils/useLocalStorage";
import { useGlobal } from "./GeneralContext";
import { useInvitation } from "./InvitationContext";
import { useUser } from "./UserContext";

interface IAuthContext {
  isAuthLoading: boolean;
  setAuthLoading: (isLoading: boolean) => void;
  handleLogout: () => void;
  handleLogin: () => void;
  isLoggedIn: boolean;
  fetchUserData: (eventIdOverride?: string) => Promise<boolean | undefined>;
}

const initialData: IAuthContext = {
  isAuthLoading: true,
  setAuthLoading: () => {},
  handleLogout: () => {},
  handleLogin: () => {},
  isLoggedIn: false,
  fetchUserData: async (_eventIdOverride?: string) => undefined,
};

const AuthContext = createContext<IAuthContext>(initialData);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useLocalStorage<null | string>(
    LOCAL_STORAGE_KEYS.token,
    null,
  );
  const [id, setId] = useLocalStorage<null | string>(
    LOCAL_STORAGE_KEYS.id,
    null,
  );
  const [_name, setName] = useLocalStorage<null | string>(
    LOCAL_STORAGE_KEYS.name,
    null,
  );
  const [_eventCode, setEventCode] = useLocalStorage<null | string>(
    LOCAL_STORAGE_KEYS.eventCode,
    null,
  );
  const [_isActive, setIsActive] = useLocalStorage<null | string>(
    LOCAL_STORAGE_KEYS.isActive,
    null,
  );
  const [_registerPageStage, setRegisterPageStage] = useLocalStorage<
    null | string
  >(LOCAL_STORAGE_KEYS.registerPageStage, null);

  const { isLoggedIn, setIsLoggedIn } = useGlobal();
  const { setUserData, setFormCompleted } = useUser();
  const navigate = useNavigate();
  const { navigateWithEvent, isCurrentPage, eventId } = useEventRouting();
  const [isLoading, setLoading] = useState(true);
  const { search } = useLocation();
  const loginNavigator = useNavigationPostLogin();

  const isInVisitorRegistration = useMemo(
    () => isCurrentPage(ROUTES.registerLink.base, { exact: false }),
    [isCurrentPage],
  );

  const fetchUserData = useTryCatch(
    async (eventIdOverride?: string) => {
      const effectiveEventId = eventIdOverride ?? eventId;
      if (!id || !effectiveEventId) {
        return;
      }
      const api = await getProfile(id, "user", effectiveEventId);
      if (api.ok === false) {
        localStorage.clear();
        return;
      }
      let { data } = api;
      if (data.hasOwnProperty("data")) {
        data = data.data;
      }
      setUserData((prev: any) => ({
        ...prev,
        ...data,
      }));
      setFormCompleted(data.is_form_completed);
      handleLogin();
      return data.is_form_completed;
    },
    { setLoading },
  );
  /**
   * Drops the stored session through the useLocalStorage setters so every
   * consumer's state updates too — a raw localStorage.clear() leaves their
   * copies stale, and they keep firing session-scoped requests.
   */
  const clearStoredSession = () => {
    setToken(null);
    setId(null);
    setName(null);
    setEventCode(null);
    setIsActive(null);
    setRegisterPageStage(null);
  };

  const handleLoginThroughTokenURL = async () => {
    const { JWToken: token, location } = extractToken(search);

    const { data, ok } = await loginMagicLink(token);
    if (!ok) return navigateWithEvent(ROUTES.base, { replace: true });
    // Contract M1: magic-link can return the step response too — the account
    // must set a new password before receiving a session. No temp-password
    // hash on this path (clear any stale one from an abandoned login-step
    // attempt or the pre-check would compare against the wrong password), so
    // the same_as_temporary check is server-only here.
    if (data.is_password_reset_required && data.token) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.stepToken, data.token);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.tempPasswordHash);
      // A step token is not a session. loginMagicLink deliberately skips
      // localStorage.clear() (it breaks the sign-up flow), so any leftover
      // session survives here — and the reset already killed it server-side.
      // Clearing through the setters (not a raw clear) also syncs the other
      // useLocalStorage consumers, so GeneralContext stops treating it as a
      // session and firing getEvents, whose 401 wipes storage and hard
      // redirects to "/" before this screen can render.
      clearStoredSession();
      return navigateWithEvent(ROUTES.createNewPassword, { replace: true });
    }
    // Normal magic-link login: drop any stale step state so the force-guard
    // can't hijack this fresh session into a dead step token (whose 401
    // handling would then wipe the new session's localStorage).
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.stepToken);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.tempPasswordHash);
    setToken(data.token);
    setId(data.hash_id);
    setName(data.first_name);
    setEventCode(data.event_token);
    setIsActive(data.is_active);
    setRegisterPageStage(data.token ? "verify" : "signup");
    if (data.user_events.length > 0) {
      handleLogin();
      navigateWithEvent(`${ROUTES.welcome}`);
    } else {
      const freshIsFormCompleted = await fetchUserData();
      loginNavigator(location, freshIsFormCompleted);
    }
    if (location.startsWith("unsub")) {
      return navigateWithEvent(ROUTES.unsubscribe);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    setIsLoggedIn(false);
    if (!isInVisitorRegistration) {
      logout(token);
    }
    localStorage.clear();
    // A leftover step token would force the next visitor of this tab back
    // into the create-new-password screen with a dead token.
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.stepToken);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.tempPasswordHash);
    navigateWithEvent(ROUTES.login);
    setLoading(false);
    return;
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    setLoading(false);
  };

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      const { JWToken } = extractToken(search);
      if (JWToken.length > 0) {
        await handleLoginThroughTokenURL();
      } else if (sessionStorage.getItem(SESSION_STORAGE_KEYS.stepToken)) {
        // Mid password-reset: the step token is the only credential and the
        // backend 401s every session endpoint until the change is done. Skip
        // auth bootstrapping entirely — any redirect here (event switcher,
        // login) tears down the Create New Password screen. Checked on the
        // token itself, not the route: React Router's pathname is not yet
        // settled when this mount effect runs.
        setLoading(false);
      } else {
        const isCached = !!token && !!id;
        if (isCached) {
          if (isOnEventSwitcher()) {
            handleLogin();
          } else {
            if (isOnNoAccessPage()) {
              setLoading(false);
              return;
            }
            // Stale credentials must not bounce the reset form to the
            // switcher. (create-new-password is handled earlier, on the step
            // token itself.)
            if (isCurrentPage(ROUTES.forgotPassword)) {
              setLoading(false);
              return;
            }
            if (!eventId) {
              redirectToEventSwitcher();
              setLoading(false);
              return;
            }
            try {
              await fetchUserData(eventId);
            } catch {
              const pathname = window.location.pathname || "";
              if (pathname === "/") {
                redirectToNoAccessPage();
              }
            }
          }
        } else {
          if (!isInVisitorRegistration) {
            const pathname = window.location.pathname;
            const hasTokenAndId = !!token && !!id;
            if (hasTokenAndId && isOnEventSwitcher()) {
              handleLogin();
              setLoading(false);
              return;
            }
            // Only redirect if not already on login page
            const isOnLoginPage =
              pathname === "/" || /^\/\d+\/?$/.test(pathname);
            if (
              !isOnLoginPage &&
              !isOnNoAccessPage() &&
              !isCurrentPage(ROUTES.webpages.base(""), { exact: false }) &&
              !isCurrentPage(ROUTES.forgotPassword) &&
              !isCurrentPage(ROUTES.createNewPassword)
            ) {
              navigateWithEvent("/");
            }
            const localstorageKeys = Object.keys(localStorage);
            localstorageKeys.forEach((key) => {
              if (key !== "envs" && key !== "env_version") {
                localStorage.removeItem(key);
              }
            });
          }
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    // Contract M1: while a step token is held, the create-new-password screen
    // cannot be skipped or navigated away from. The token dies on success,
    // on 401 (expired/used), or with the tab.
    const stepToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.stepToken);
    if (stepToken) {
      // Legal/CMS pages stay reachable (unsubscribe links must always work);
      // everything else is forced back to the screen, and no other redirect
      // branch below may run while the step flow owns navigation — otherwise
      // the eventCode → register-link branch ping-pongs against this guard.
      const onExemptPage =
        isCurrentPage(ROUTES.unsubscribe) ||
        isCurrentPage(ROUTES.webpages.base(""), { exact: false });
      if (!onExemptPage) {
        if (!isCurrentPage(ROUTES.createNewPassword)) {
          navigateWithEvent(ROUTES.createNewPassword, { replace: true });
        }
        return;
      }
    }
    const rawIsActive = localStorage.getItem(LOCAL_STORAGE_KEYS.isActive);
    // Treat absence (null) as undefined, not as inactive. Only explicit "true"/"false" strings map to booleans.
    const isUserActive =
      rawIsActive === null ? undefined : rawIsActive === "true";
    const eventCode = localStorage.getItem(LOCAL_STORAGE_KEYS.eventCode);
    const registerPageStage = localStorage.getItem(
      LOCAL_STORAGE_KEYS.registerPageStage,
    );
    if (isLoggedIn && !eventId && !isLoading) {
      if (isOnNoAccessPage()) {
        return;
      }
      // A logged-in user opening a reset link from the email must still
      // reach the form — don't bounce them to the event switcher.
      if (isCurrentPage(ROUTES.createNewPassword)) {
        return;
      }
      if (!isCurrentPage(ROUTES.eventSwitcher)) {
        navigate(ROUTES.eventSwitcher, { replace: true });
      }
      return;
    }
    if (
      token &&
      isUserActive === false &&
      !isCurrentPage(ROUTES.base) &&
      !isCurrentPage(ROUTES.forgotPassword) &&
      !isCurrentPage(ROUTES.createNewPassword)
    ) {
      if (registerPageStage === "verify") {
        return navigateWithEvent(ROUTES.registerLink.verifyLink);
      } else {
        return navigateWithEvent(ROUTES.registerLink.signup);
      }
    }
    if (
      eventCode &&
      (!token || isUserActive === false) &&
      !isInVisitorRegistration
    ) {
      return navigateWithEvent(ROUTES.registerLink.base);
    }
    if (!isLoggedIn && !isLoading) {
      // Don't redirect to login if user has credentials and is on event switcher or no-access
      const hasStoredSession = !!token && !!id;
      const isOnRecoveryPage =
        (isOnEventSwitcher() || isOnNoAccessPage()) && hasStoredSession;
      if (isOnRecoveryPage) {
        if (isOnEventSwitcher() && hasStoredSession) {
          handleLogin();
        }
        return;
      }

      const pathname = window.location.pathname;
      const isOnLoginPage =
        isCurrentPage(ROUTES.login) || /^\/\d+\/?$/.test(pathname);
      if (isOnNoAccessPage()) {
        return;
      }
      // if the user is not logged in and not on the login page, redirect to login
      if (
        !isOnLoginPage &&
        !isInVisitorRegistration &&
        !isCurrentPage(ROUTES.webpages.base(""), { exact: false }) &&
        !isCurrentPage(ROUTES.forgotPassword) &&
        !isCurrentPage(ROUTES.createNewPassword)
      ) {
        navigateWithEvent(ROUTES.login);
      }
    }
    if (isCurrentPage(ROUTES.registerLink.base, { exact: false })) {
      const storedEventCode = normalize(
        localStorage.getItem(LOCAL_STORAGE_KEYS.eventCode),
      );
      const { event_code } = extractParamsFromURL();
      if (!event_code && !storedEventCode) {
        navigateWithEvent(ROUTES.login);
      }
    }
    if (isCurrentPage(ROUTES.registerLink.base)) {
      const storedEventCode = normalize(
        localStorage.getItem(LOCAL_STORAGE_KEYS.eventCode),
      );
      const { event_code } = extractParamsFromURL();

      if (!event_code && !storedEventCode)
        return navigateWithEvent(ROUTES.login); // if no event_code and no stored event code, redirect to login
      // if the same key is passed, do nothing.
      if (event_code === storedEventCode || (storedEventCode && !event_code))
        return;
      localStorage.setItem(LOCAL_STORAGE_KEYS.eventCode, event_code);
      navigateWithEvent(ROUTES.registerLink.base);
    }
  }, [isLoggedIn, isCurrentPage, token, eventId, isLoading]);

  const contextValue = {
    isAuthLoading: isLoading,
    setAuthLoading: setLoading,
    handleLogout,
    handleLogin,
    isLoggedIn,
    fetchUserData,
  };
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
export const useNavigationPostLogin = () => {
  const { shouldApproveInvitation } = useRouting();
  const { updateInvitation } = useInvitation();
  const [id] = useLocalStorage<string | null>(LOCAL_STORAGE_KEYS.id, null);
  const { navigateWithEvent, eventId } = useEventRouting();
  return async (
    location: string,
    freshIsFormCompleted?: boolean,
    _eventId?: string,
  ) => {
    const effectiveEventId = _eventId ?? eventId;
    if (freshIsFormCompleted) {
      const dashboardPath = effectiveEventId
        ? `/${effectiveEventId}${ROUTES.dashboard.base}`
        : ROUTES.dashboard.base;
      return navigateWithEvent(dashboardPath);
    }

    if (location && location.length > 0) {
      if (location.startsWith("pref")) {
        if (!id) {
          throw new Error("Guest ID is missing for guest preferences route");
        }
        return navigateWithEvent(ROUTES.dashboard.guestPreferences(id));
      }
      if (location.startsWith("unsub")) {
        return navigateWithEvent(ROUTES.unsubscribe);
      }
    }

    const { data } = await getInvitationDetails(effectiveEventId);
    const invitationData = data;
    updateInvitation(invitationData);

    const nextRoute = await shouldApproveInvitation(
      invitationData,
      freshIsFormCompleted,
    );
    return navigateWithEvent(_eventId ? `/${_eventId}${nextRoute}` : nextRoute);
  };
};
